// Reinforce API client.
//
// Nothing here talks to a server yet. Each function is one endpoint, so when
// the backend lands you point REACT_APP_REINFORCE_API at it and delete the mock
// branch — the components never change. See ../INTEGRATION_CHECKLIST.md.
import axios from "axios";
import { baseURL } from "aws-config";

import { COHORTS, FAQS, INSTRUCTORS, SYLLABI } from "./mockData";

const API_BASE = process.env.REACT_APP_REINFORCE_API || "";
export const USING_MOCKS = !API_BASE;

// Helper to get auth headers for authenticated requests
const authHeaders = () => {
  const token = localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LATENCY_MS = [280, 620];

// ?mockFail=cohorts renders the error state without touching code; ?mockEmpty=1
// shows the no-cohorts state. Both are development aids, harmless in prod.
const flag = (name) => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
};

const settle = (value, { failsAs } = {}) =>
  new Promise((resolve, reject) => {
    const delay = LATENCY_MS[0] + Math.random() * (LATENCY_MS[1] - LATENCY_MS[0]);
    setTimeout(() => {
      if (failsAs && flag("mockFail") === failsAs) {
        reject(new Error("Mock failure requested via ?mockFail"));
        return;
      }
      resolve(typeof value === "function" ? value() : value);
    }, delay);
  });

// What the list endpoint is expected to expand for the client: the static
// syllabus the cohort points at, and the instructor records it references.
const expand = (cohort) => ({
  ...cohort,
  syllabus: SYLLABI[cohort.syllabus_key] || [],
  instructors: (cohort.instructor_ids || []).map((id) => INSTRUCTORS[id]).filter(Boolean),
});

/** GET /reinforce/cohorts/ */
export const fetchCohorts = () => {
  if (!USING_MOCKS) return axios.get(`${API_BASE}/cohorts/`).then((r) => r.data);
  return settle(() => (flag("mockEmpty") ? [] : COHORTS.map(expand)), { failsAs: "cohorts" });
};

/** GET /reinforce/cohorts/<slug>/ */
export const fetchCohort = (slug) => {
  if (!USING_MOCKS) return axios.get(`${API_BASE}/cohorts/${slug}/`).then((r) => r.data);
  const cohort = COHORTS.find((c) => c.slug === slug);
  if (!cohort) return Promise.reject(new Error("No such cohort"));
  return settle(() => expand(cohort));
};

/** GET /reinforce/faqs/ — static for now, still async so the page cannot assume. */
export const fetchFaqs = () => {
  if (!USING_MOCKS) return axios.get(`${API_BASE}/faqs/`).then((r) => r.data);
  return settle(FAQS);
};

/** POST /reinforce/waitlist/ */
export const joinWaitlist = (payload) => {
  if (!USING_MOCKS) return axios.post(`${API_BASE}/waitlist/`, payload).then((r) => r.data);
  return settle({ id: Math.floor(Math.random() * 9000) + 1000, ...payload, createdAt: new Date().toISOString() });
};

/** Someone wants a call before they commit.
 *
 * This is the one form on the page that is NOT mocked. The existing backend
 * already has request-call-details/, which alerts Telegram and emails the team
 * the moment it is hit — so a lead here reaches a human today rather than
 * waiting for the Reinforce backend. It goes to the same inbox as the 1:1
 * callbacks, tagged by program_name so they can be told apart.
 */
export const requestCallback = ({ name, phone, cohortTitle }) =>
  axios
    .post(`${baseURL}request-call-details/`, {
      name,
      phone,
      program_name: cohortTitle ? `Reinforce — ${cohortTitle}` : "Reinforce (cohort enquiry)",
    })
    .then((r) => r.data);

/** POST /reinforce/enrollments/ — creates the pending enrollment a payment attaches to. */
export const createEnrollment = (payload) => {
  if (!USING_MOCKS) {
    // Backend expects snake_case field names
    const body = { cohort_slug: payload.cohortSlug };
    return axios.post(`${API_BASE}/enrollments/`, body, { headers: authHeaders() }).then((r) => r.data);
  }
  const cohort = COHORTS.find((c) => c.slug === payload.cohortSlug);
  const fee = cohort?.fee || {};
  const amount = fee.earlyBird || fee.amount || 0;
  return settle({
    id: `ENR-MOCK-${Math.floor(Math.random() * 90000) + 10000}`,
    status: "pending_payment",
    amount,
    currency: fee.currency || "INR",
    cohortSlug: payload.cohortSlug,
  });
};

/** POST /reinforce/payments/verify/ — Razorpay signature check after checkout.
 *
 * Mirrors the interview-prep flow (flowstateApi.verifyPayment), which posts
 * razorpay_order_id / razorpay_payment_id / razorpay_signature and gets back a
 * paid enrolment. Mocked here: it always succeeds and charges nothing.
 */
/**
 * POST /reinforce/payments/verify/ — Razorpay signature check after checkout.
 * Called after Razorpay checkout returns with payment details.
 */
export const verifyPayment = ({ razorpayOrderId, razorpayPaymentId, razorpaySignature }) => {
  if (!USING_MOCKS) {
    const body = {
      razorpay_order_id: razorpayOrderId,
      razorpay_payment_id: razorpayPaymentId,
      razorpay_signature: razorpaySignature,
    };
    return axios.post(`${API_BASE}/payments/verify/`, body, { headers: authHeaders() }).then((r) => r.data);
  }
  return settle({
    razorpayOrderId,
    razorpayPaymentId,
    status: "paid",
    mock: true,
  });
};

/**
 * Open Razorpay checkout modal and return payment result.
 */
export const openRazorpayCheckout = ({ orderId, amount, currency, keyId, userEmail, userName, cohortTitle }) => {
  return new Promise((resolve, reject) => {
    if (USING_MOCKS) {
      // Mock: simulate successful payment after short delay
      return settle({
        razorpay_order_id: orderId,
        razorpay_payment_id: `pay_mock_${Math.random().toString(36).slice(2, 12)}`,
        razorpay_signature: "mock_signature",
      }).then(resolve).catch(reject);
    }

    // Load Razorpay script if not already loaded
    const loadRazorpay = () => {
      return new Promise((res, rej) => {
        if (window.Razorpay) return res();
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = res;
        script.onerror = rej;
        document.body.appendChild(script);
      });
    };

    loadRazorpay()
      .then(() => {
        const options = {
          key: keyId,
          amount: amount, // In paise
          currency: currency,
          name: "Reinforce by Edgent",
          description: cohortTitle || "Cohort Enrollment",
          order_id: orderId,
          prefill: {
            email: userEmail || "",
            name: userName || "",
          },
          theme: { color: "#141414" },
          handler: (response) => {
            resolve({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
          },
          modal: {
            ondismiss: () => {
              reject(new Error("Payment cancelled"));
            },
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (response) => {
          reject(new Error(response.error?.description || "Payment failed"));
        });
        rzp.open();
      })
      .catch(reject);
  });
};

/** POST /reinforce/payments/order/ — would return a Razorpay order to hand to checkout. */
export const createPaymentOrder = ({ enrollmentId, amount, currency = "INR" }) => {
  if (!USING_MOCKS) {
    // Backend expects snake_case field names
    const body = { enrollment_id: enrollmentId, amount, currency };
    return axios.post(`${API_BASE}/payments/order/`, body, { headers: authHeaders() }).then((r) => r.data);
  }
  return settle({
    orderId: `order_mock_${Math.random().toString(36).slice(2, 12)}`,
    enrollmentId,
    amount,
    currency,
    // No gateway is wired up. The UI shows this plainly rather than pretending.
    mock: true,
  });
};
